// src/pages/PainelCompraConsumo.jsx
import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Stack,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  LinearProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
dayjs.extend(customParseFormat);

// nomes de mês e dia
const MESES_EXT = [
  "JANEIRO","FEVEREIRO","MARÇO","ABRIL","MAIO","JUNHO",
  "JULHO","AGOSTO","SETEMBRO","OUTUBRO","NOVEMBRO","DEZEMBRO"
];
const WD_PT = ["Dom","Seg","Ter","Qua","Qui","Sex","Sáb"];
// perto do WD_PT
const weekdayPt = (dIdx) => {
  const s = WD_PT[dIdx] || "";
  return s === "Sexo" ? "Sex" : s;
};


// número seguro (aceita "R$ 41.671,00", "41671.00", etc.)
const N = (v) => {
  if (v == null) return 0;
  if (typeof v === "string") {
    const only = v.replace(/[^\d.,-]/g, "");   // remove "R$", espaços, etc.
    const norm = only.replace(/\./g, "").replace(",", ".");
    const n = Number(norm);
    return Number.isFinite(n) ? n : 0;
  }
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const BRL = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(N(v));

/** Extrai "YYYY-MM-DD" apenas da coluna DATA */
function extractYMDFromDATA(row) {
  const raw = row?.DATA;
  if (!raw) return null;
  const s = String(raw).trim();

  // 1) "2025-01-02T04:00:00.000Z" ou "2025-01-02"
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;

  // 2) "2025/01/02"
  m = s.match(/(\d{4})\/(\d{2})\/(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;

  // 3) "02/01/2025"
  m = s.match(/(\d{2})\/(\d{2})\/(\d{4})/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;

  // 4) "20250102"
  m = s.match(/(^|[^\d])(\d{4})(\d{2})(\d{2})(?!\d)/);
  if (m) return `${m[2]}-${m[3]}-${m[4]}`;

  return null;
}

/** Data-base do registro como dayjs (usa **somente** DATA) */
function getRowDate(row) {
  const ymd = extractYMDFromDATA(row);
  if (!ymd) return null;
  const d = dayjs(ymd, "YYYY-MM-DD", true);
  return d.isValid() ? d : null;
}

/** Gera TODOS os dias do mês (inclui sábado e domingo) */
function buildAllDays(ano, mes1to12) {
  const first = dayjs(`${ano}-${String(mes1to12).padStart(2, "0")}-01`).startOf("day");
  const last  = first.endOf("month").startOf("day");
  const out = [];
  let cur = first;
  while (!cur.isAfter(last)) {     // <= último dia do mês
    out.push(cur);
    cur = cur.add(1, "day");
  }
  return out;
}

// Normalização e detecção de bucket tolerante
const norm = (v) =>
  String(v ?? "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();

function bestGroupLabel(row) {
  const cand = [
    row?.GR_ANALISE,
    row?.GR_ESTOQUE,
    row?.GR_DESPESA,
    row?.GR_COMPRA,
    row?.TIPO_ITEM,
    row?.GRUPO, // se existir
  ];
  for (const c of cand) {
    const s = norm(c);
    if (s) return s;
  }
  return "";
}

function bucketFromRow(row) {
  const s = bestGroupLabel(row);
  if (!s) return null;

  if (/(^|\W)PNEU(S)?(\W|$)/.test(s)) return "PNEUS";
  if (/(^|\W)PECA(S)?(\W|$)/.test(s)) return "PEÇAS";
  if (/(COMB(USTIVEL)?|LUBR(IFICANTE|IFICANTES)?)/.test(s))
    return "COMBUSTÍVEL / LUBRIFICANTES";
  if (/(ADMIN(ISTRATIVO)?|PREDIAL)/.test(s))
    return "ADMINISTRATIVO / PREDIAL";

  // Prefixos “001/002/003/004 - ...”
  if (/001/.test(s) && /(COMB|LUBR)/.test(s))
    return "COMBUSTÍVEL / LUBRIFICANTES";
  if (/002/.test(s) && /PNEU/.test(s)) return "PNEUS";
  if (/003/.test(s) && /PECA/.test(s)) return "PEÇAS";
  if (/004/.test(s) && /(ADMIN|PREDIAL)/.test(s))
    return "ADMINISTRATIVO / PREDIAL";

  // fallback não destrutivo (evita perder compra válida)
  return "ADMINISTRATIVO / PREDIAL";
}

/**
 * Painel Diário de Compras:
 *  - Soma somente VALOR_COMPRA > 0 por dia e por bucket.
 *  - Data-base: **sempre** a coluna DATA (DT_ATUALIZACAO não é usada).
 *  - Exibe **todos os dias** do mês (seg a dom).
 *  - (Opcional) Faz auto-paginação no backend, se `fetchUrl` for informado.
 */
export default function PainelCompraConsumo({
  open,
  onClose,
  ano,
  mes,
  rows = [],

  // auto-load opcional (usa o endpoint do seu backend)
  fetchUrl,                  // ex.: "/api/setor2/compra-consumo"
  startDate,                 // "YYYY-MM-DD"
  endDate,                   // "YYYY-MM-DD"
  pageLimit = 5000,          // mesmo do Postman
  enableAutoLoad = true,     // liga/desliga auto-paginação

  valorKey = "VALOR_COMPRA", // compras
}) {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState([]);

  // Auto-paginação (se fetchUrl/startDate/endDate fornecidos)
  useEffect(() => {
    let abort = false;
    async function loadAll() {
      if (!enableAutoLoad || !fetchUrl) {
        setFetched([]);
        return;
      }
      // Se não vierem datas, derive do ano/mês do painel
      const mm = String(mes).padStart(2, "0");
      const derivedStart = `${ano}-${mm}-01`;
      const derivedEnd = dayjs(derivedStart).endOf("month").format("YYYY-MM-DD");
      const sDate = startDate || derivedStart;
      const eDate = endDate   || derivedEnd;
      setLoading(true);
      try {
        let all = [];
        let offset = 0;
        // evita loop infinito com um hard cap
        const MAX_PAGES = 200; // 200 * 5000 = 1 milhão de linhas
        const MAX_RETRIES = 2;
        for (let page = 0; page < MAX_PAGES; page++) {
          const body = JSON.stringify({
            startDate: sDate,
            endDate: eDate,
            limit: pageLimit,
            offset,
          });

          let data = null;
          for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
            try {
              const res = await fetch(fetchUrl, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: "chave-super-secreta-123",
                },
                body,
              });
              if (!res.ok) {
                throw new Error(`HTTP ${res.status}: ${res.statusText}`);
              }
              data = await res.json();
              break; // sucesso
            } catch (fetchErr) {
              console.warn(
                `⚠️ PainelCompraConsumo fetch tentativa ${attempt + 1}/${MAX_RETRIES + 1}:`,
                fetchErr.message
              );
              if (attempt === MAX_RETRIES) throw fetchErr;
              // espera progressiva antes de retry
              await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
            }
          }

          if (abort) return;
          if (!Array.isArray(data) || data.length === 0) break;
          all = all.concat(data);
          if (data.length < pageLimit) break; // acabou
          offset += pageLimit;
        }
        if (!abort) setFetched(all);
      } catch (e) {
        console.error("Falha ao carregar dados do painel:", e);
        if (!abort) setFetched([]);
      } finally {
        if (!abort) setLoading(false);
      }
    }
    loadAll();
    return () => { abort = true; };
  }, [enableAutoLoad, fetchUrl, startDate, endDate, pageLimit, ano, mes]);

  // escolhe a fonte de dados: auto-load (se existir) > rows prop
  const dataRows = fetchUrl ? fetched : rows;

  const titulo = useMemo(() => {
    const nomeMes = MESES_EXT[Math.max(1, Math.min(12, mes)) - 1] || "";
    return `PAINEL DIÁRIO DE COMPRAS — ${nomeMes} ${ano}`;
  }, [ano, mes]);

  // filtra pelo mês/ano usando **somente DATA**
  const linhasMes = useMemo(() => {
    return dataRows.filter((r) => {
      const d = getRowDate(r);
      return d && d.year() === Number(ano) && d.month() + 1 === Number(mes);
    });
  }, [dataRows, ano, mes]);

  // agrega por dia → bucket
  const porDia = useMemo(() => {
    const map = new Map(); // Map("YYYY-MM-DD" -> Map(bucket -> valor))
    for (const r of linhasMes) {
      const d = getRowDate(r);
      if (!d) continue;
      const key = d.format("YYYY-MM-DD");

      const v = N(r?.[valorKey]);      // inclui positivos, zeros e negativos
      if (!Number.isFinite(v)) continue;

      const b = bucketFromRow(r);
      if (!b) continue;

      if (!map.has(key)) map.set(key, new Map());
      const inner = map.get(key);
      inner.set(b, (inner.get(b) || 0) + v);
    }
    return map;
  }, [linhasMes, valorKey]);

  // agora inclui TODOS os dias do mês (seg–dom)
  const dias = useMemo(() => buildAllDays(ano, mes), [ano, mes]);

  const linhas = useMemo(() => {
    return dias
      .map((d) => {
        const chave   = d.format("YYYY-MM-DD");
        const buckets = porDia.get(chave);

        const pecas = buckets?.get("PEÇAS") || 0;
        const pneus = buckets?.get("PNEUS") || 0;
        const comb  = buckets?.get("COMBUSTÍVEL / LUBRIFICANTES") || 0;
        const adm   = buckets?.get("ADMINISTRATIVO / PREDIAL") || 0;

        const totalNum = pecas + pneus + comb + adm;

        return {
          date: d,                      // objeto dayjs (para weekday etc.)
          data: d.format("DD/MM"),      // <— string que a coluna DATA usa
          diaSem: WD_PT[d.day()],
          "PEÇAS": pecas,
          "PNEUS": pneus,
          "COMBUSTÍVEL / LUBRIFICANTES": comb,
          "ADMINISTRATIVO / PREDIAL": adm,
          totalNum,
          total: totalNum,
        };
      })
      .filter((ln) => ln.totalNum > 0);
  }, [dias, porDia]);



  // resumo topo
  const hoje = dayjs();
  const mesmoMesAno =
    hoje.year() === Number(ano) && hoje.month() + 1 === Number(mes);
  const limite = mesmoMesAno
    ? hoje.endOf("day")
    : dayjs(`${ano}-${String(mes).padStart(2, "0")}-01`).endOf("month");
  const acumuladoAteHoje = useMemo(() => {
    let sum = 0;
    for (const ln of linhas) {
      if (ln.date.isBefore(limite.add(1, "ms"))) sum += ln.total;
    }
    return sum;
  }, [linhas, limite]);
  // totais do mês (somam todas as linhas do grid)
  const totaisMes = useMemo(() => {
    let pecas = 0, pneus = 0, comb = 0, adm = 0, total = 0;
    for (const ln of linhas) {
      pecas += N(ln["PEÇAS"]);
      pneus += N(ln["PNEUS"]);
      comb  += N(ln["COMBUSTÍVEL / LUBRIFICANTES"]);
      adm   += N(ln["ADMINISTRATIVO / PREDIAL"]);
      total += N(ln.total);
    }
    return { pecas, pneus, comb, adm, total };
  }, [linhas]);


  // --- UI ---
  const center = { textAlign: "center" };
  const money = { fontVariantNumeric: "tabular-nums", textAlign: "center" };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ textAlign: "center", fontWeight: 800, pr: 6 }}>
        {titulo}
        <IconButton
          onClick={onClose}
          aria-label="Fechar"
          sx={{ position: "absolute", right: 8, top: 8 }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ display: "flex", flexDirection: "column", p: 2 }}
      >
        {loading && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
          </Box>
        )}

        {/* Resumo topo */}
        <Stack direction="row" alignItems="stretch" sx={{ mb: 2 }}>
          {/* espaço à esquerda para equilibrar visualmente */}
          <Box sx={{ flex: 1 }} />

          {/* quatro cards centrais, alinhados e com espaçamento uniforme */}
          <Stack
            direction="row"
            sx={{
              flex: 2,
              justifyContent: "center",
              alignItems: "center",
              columnGap: 6,
              textAlign: "center",
            }}
          >
            <Stack spacing={0.5} sx={{ minWidth: 180 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.3 }}>
                PEÇAS
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {BRL(totaisMes.pecas)}
              </Typography>
            </Stack>

            <Stack spacing={0.5} sx={{ minWidth: 180 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.3 }}>
                PNEUS
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {BRL(totaisMes.pneus)}
              </Typography>
            </Stack>

            <Stack spacing={0.5} sx={{ minWidth: 220 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.3 }}>
                COMBUSTÍVEL/LUBRIFICANTES
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {BRL(totaisMes.comb)}
              </Typography>
            </Stack>

            <Stack spacing={0.5} sx={{ minWidth: 220 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.3 }}>
                ADMINISTRATIVO/PREDIAL
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {BRL(totaisMes.adm)}
              </Typography>
            </Stack>
          </Stack>

          {/* acumulado geral à direita */}
          <Box sx={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Stack spacing={0.5} sx={{ textAlign: "center", width: "100%" }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: theme.palette.text.secondary }}>
                ACUMULADO DE{" "}
                {mesmoMesAno ? hoje.format("DD/MM/YYYY") : `${MESES_EXT[mes - 1]}`}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {BRL(acumuladoAteHoje)}
              </Typography>
            </Stack>
          </Box>
        </Stack>

        <Divider sx={{ mb: 1.5 }} />

        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ flex: 1, overflow: "auto" }}   // rolagem só aqui
        >
          <Table size="small" stickyHeader>
            <TableHead
              sx={(theme) => ({
                position: "sticky",
                top: 0,
                "& th": {
                  backgroundColor: theme.palette.background.paper,
                  zIndex: 1,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                },
                boxShadow: `0 1px 0 ${theme.palette.divider}`,
              })}
            >
              <TableRow>
                <TableCell sx={{ ...center, fontWeight: 800 }} width={100}>
                  DATA
                </TableCell>
                <TableCell sx={{ ...center, fontWeight: 800 }} width={90}>
                  D.SEMANA
                </TableCell>
                <TableCell sx={{ ...center, fontWeight: 800 }}>PEÇAS</TableCell>
                <TableCell sx={{ ...center, fontWeight: 800 }}>PNEUS</TableCell>
                <TableCell sx={{ ...center, fontWeight: 800 }}>
                  COMBUSTÍVEL/LUBRIFICANTES
                </TableCell>
                <TableCell sx={{ ...center, fontWeight: 800 }}>
                  ADMINISTRATIVO/PREDIAL
                </TableCell>
                <TableCell sx={{ ...center, fontWeight: 800 }}>TOTAL</TableCell>
              </TableRow>
            </TableHead>
            <TableBody
              sx={(theme) => ({
                // linhas ímpares listradas
                "& tr:nth-of-type(odd) td": {
                  backgroundColor: theme.palette.action.hover,
                },
                // realce ao passar o mouse
                "& tr:hover td": {
                  backgroundColor: theme.palette.action.selected,
                },
              })}
            >
              {linhas.map((ln) => (
                <TableRow key={ln.date.toISOString()}>
                  <TableCell sx={center}>{ln.data}</TableCell>
                  <TableCell sx={center}><span translate="no">{weekdayPt(ln.date.day())}</span></TableCell>
                  <TableCell sx={money}>{BRL(ln["PEÇAS"])}</TableCell>
                  <TableCell sx={money}>{BRL(ln["PNEUS"])}</TableCell>
                  <TableCell sx={money}>
                    {BRL(ln["COMBUSTÍVEL / LUBRIFICANTES"])}
                  </TableCell>
                  <TableCell sx={money}>
                    {BRL(ln["ADMINISTRATIVO / PREDIAL"])}
                  </TableCell>
                  <TableCell sx={{ ...money, fontWeight: 800 }}>
                    {BRL(ln.total)}
                  </TableCell>
                </TableRow>
              ))}
              {!linhas.length && !loading && (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    align="center"
                    sx={{ color: theme.palette.text.secondary, py: 4 }}
                  >
                    Sem dados para o período.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ mt: 1.5, textAlign: "center" }}>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Observação: o painel exibe apenas os dias com compras registradas.
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
